/**
 * @file `useMe` — TanStack-backed read of the current admin identity.
 * @module @labcraft/admin/ui/hooks/use-me
 *
 * Replaces the old `useAdmin()` external-store subscription. The hook
 * returns the wire-shape `{ admin: Session | null }` (so anonymous is
 * a payload value, not a loading state); callers branch on
 * `data?.admin` to render the dashboard vs the sign-in card.
 *
 * The 401 callback wired in `main.tsx` calls
 * `setQueryData(qkMe, { admin: null })` synchronously when any
 * authenticated request returns 401. That flip makes this hook
 * re-render with `admin: null` on the next commit — no refetch
 * required. TanStack will also refetch normally on the next mount
 * / refocus per the workspace's `defaultOptions.queries.staleTime`.
 *
 * Convention: hooks live in this directory and never call `useQuery`
 * outside it. Components import workspace hooks (`useMe`,
 * `useLearners`, …) only.
 */

import { useQuery } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Admin } from '../../client/index.ts'
import type { MeResponse } from '../../shared/routes.ts'
import { qkMe } from './query_keys.ts'

/**
 * Subscribe to the current `MeResponse` from React. The first reader
 * drives the fetch; subsequent readers share the cache. The 401
 * callback's imperative `setQueryData(qkMe, { admin: null })` flips
 * every reader synchronously on the next commit.
 */
export function useMe() {
  const client = useClient<Admin>()
  return useQuery<MeResponse>({
    queryKey: qkMe,
    queryFn: () => client.me(),
  })
}
